import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { formatNyDateTime } from '../../utils/datetime';

const RentalHistoryModal = ({ isOpen, onClose, rental, truck, onViewImage, onAddComment, language = 'pt-BR' }) => {
  const [comentario, setComentario] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [viewingImage, setViewingImage] = React.useState(null);

  if (!rental || !truck) return null;

  const tipoIcons = {
    info_geral: '📢',
    problema: '⚠️',
    manutencao_realizada: '🔧',
    outro: '📝'
  };

  const tipoColors = {
    info_geral: 'bg-blue-50 border-blue-200',
    problema: 'bg-red-50 border-red-200',
    manutencao_realizada: 'bg-green-50 border-green-200',
    outro: 'bg-gray-50 border-gray-200'
  };

  const handleComment = async (updateId) => {
    if (!comentario.trim()) {
      toast.error(language === 'pt-BR' ? 'Digite um comentário' : 'Enter a comment');
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      await axios.put(`${API_URL}/api/rentals/${rental.id}/updates/${updateId}/comment`, 
        { comentario },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(language === 'pt-BR' ? '✅ Comentário adicionado' : '✅ Comment added');
      setComentario('');
      if (onAddComment) onAddComment();
    } catch (error) {
      toast.error(language === 'pt-BR' ? 'Erro ao comentar' : 'Comment error');
    } finally {
      setLoading(false);
    }
  };

  const updates = rental.updates || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            📋 {language === 'pt-BR' ? 'Histórico' : 'History'} - {truck.modelo} ({truck.placa})
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-gray-50 p-3 rounded">
            <p className="font-semibold">{rental.pm_name}</p>
            <p className="text-sm text-gray-600">
              {new Date(rental.check_in?.data || rental.data_solicitacao).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })} - 
              {rental.status === 'ativo' ? (language === 'pt-BR' ? ' Em andamento' : ' In progress') : new Date(rental.check_out?.data).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })}
            </p>
          </div>

          {updates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>{language === 'pt-BR' ? 'Nenhuma atualização registrada' : 'No updates recorded'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {updates.map((update, idx) => (
                <div key={update.id || idx} className={`border rounded-lg p-4 ${tipoColors[update.tipo]}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold flex items-center gap-2">
                        {tipoIcons[update.tipo]} {update.titulo}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {formatNyDateTime(update.data_hora)} • {update.reportado_por_nome}
                      </p>
                    </div>
                    {update.custo_adicional > 0 && (
                      <Badge className="bg-green-600 text-white">${update.custo_adicional}</Badge>
                    )}
                  </div>

                  <p className="text-sm mt-2">{update.descricao}</p>

                  {update.localizacao && (
                    <p className="text-xs text-gray-600 mt-2">📍 {update.localizacao}</p>
                  )}

                  {update.fotos && update.fotos.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {update.fotos.map((foto, fotoIdx) => (
                        <img 
                          key={fotoIdx}
                          src={foto} 
                          alt={`Foto ${fotoIdx + 1}`}
                          className="w-24 h-24 object-cover rounded border cursor-pointer hover:opacity-75 transition hover:scale-105"
                          onClick={() => onViewImage && onViewImage(foto)}
                        />
                      ))}
                    </div>
                  )}

                  {update.admin_comentario && (
                    <div className="mt-3 bg-white p-3 rounded border">
                      <p className="text-xs font-semibold text-blue-600">💬 Admin:</p>
                      <p className="text-sm mt-1">{update.admin_comentario}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatNyDateTime(update.admin_comentou_em)}</p>
                    </div>



                  )}
                </div>
              ))}

              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm font-semibold">
                  💰 {language === 'pt-BR' ? 'Total de custos adicionais:' : 'Total additional costs:'} ${rental.total_custos_adicionais?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {language === 'pt-BR' ? 'Fechar' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RentalHistoryModal;